/**
 * Disbursements Tour Configuration
 * 
 * Detailed step-by-step tour guide for the Disbursements page,
 * explaining tabs, form fields for both single and bulk disbursements,
 * and the transaction history section.
 */

export const getDisbursementsTourSteps = (t) => {
  // Helper function to check if an element exists in the DOM
  const elementExists = (selector) => {
    if (typeof document === 'undefined') {return false;}
    const element = document.querySelector(selector);
    return element !== null && element.offsetParent !== null;
  };

  // Base steps that always appear
  const baseSteps = [
    // Tabs section - positioned to highlight the tab buttons
    {
      selector: '[data-tour="disbursements-tabs"]',
      content: t('tour.disbursements.tabs', {
        ns: 'common',
        defaultValue: 'Choose between Single or Bulk disbursement mode.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '-10px'
        }),
        maskWrapper: (base) => ({
          ...base,
          transform: 'translateY(-27px)'
        }),
        maskArea: (base) => ({
          ...base,
          transform: 'translateY(-27px)'
        })
      }
    }
  ];

  // Single disbursement form steps - Section level only
  const singleFormSteps = [
    // Source Account Section
    {
      selector: '[data-tour="disbursements-source-account"]',
      content: t('tour.disbursements.sourceAccount', {
        ns: 'common',
        defaultValue: 'Select the account to disburse from (wallet balance).'
      }),
      position: 'bottom'
    },
    
    // Payment Details Section
    {
      selector: '[data-tour="disbursements-payment-details"]',
      content: t('tour.disbursements.paymentDetails', {
        ns: 'common',
        defaultValue: 'Enter payment amount and purpose/narration.'
      }),
      position: 'bottom'
    },
    {
      selector: '[data-tour="disbursements-amount"]',
      content: t('tour.disbursements.amount', {
        ns: 'common',
        defaultValue: 'Set the amount you want to send to the recipient.'
      }),
      position: 'bottom'
    },
    {
      selector: '[data-tour="disbursements-purpose"]',
      content: t('tour.disbursements.purpose', {
        ns: 'common',
        defaultValue: 'Add a purpose or narration for this disbursement.'
      }),
      position: 'bottom'
    },
    
    // Destination Account Section
    {
      selector: '[data-tour="disbursements-destination-account"]',
      content: t('tour.disbursements.destinationAccount', {
        ns: 'common',
        defaultValue: 'Select payment method and enter recipient details.'
      }),
      position: 'bottom'
    },
    {
      selector: '[data-tour="disbursements-payment-method"]',
      content: t('tour.disbursements.paymentMethod', {
        ns: 'common',
        defaultValue: 'Choose how to send the money (Mobile Money or Bank).'
      }),
      position: 'bottom'
    },
    {
      selector: '[data-tour="disbursements-recipient-name"]',
      content: t('tour.disbursements.recipientName', {
        ns: 'common',
        defaultValue: 'Recipient name details appear here after fetching account information.'
      }),
      position: 'bottom'
    },
    {
      selector: '[data-tour="disbursements-phone-number"]',
      content: t('tour.disbursements.phoneNumber', {
        ns: 'common',
        defaultValue: 'Enter the recipient phone number for Mobile Money verification.'
      }),
      position: 'bottom'
    },
    {
      selector: '[data-tour="disbursements-provider"]',
      content: t('tour.disbursements.provider', {
        ns: 'common',
        defaultValue: 'Select the Mobile Money provider for the recipient.'
      }),
      position: 'bottom'
    }
  ];

  // Bulk disbursement steps - Different workflow from single
  const bulkSteps = [
    // Bulk Upload Section - First step in bulk workflow
    {
      selector: '[data-tour="disbursements-bulk-upload"]',
      content: t('tour.disbursements.bulkUpload', {
        ns: 'common',
        defaultValue: 'Download CSV template, fill with recipient details, then upload here.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '10px'
        })
      }
    },
    
    // Bulk Preview - Only shown when file is uploaded and parsed
    {
      selector: '[data-tour="disbursements-bulk-preview"]',
      content: t('tour.disbursements.bulkPreview', {
        ns: 'common',
        defaultValue: 'Review all disbursements, totals, and fees before processing.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '10px'
        })
      }
    },
    
    // Batch Number Input - Only shown when file is uploaded and parsed
    {
      selector: '[data-tour="disbursements-bulk-batch"]',
      content: t('tour.disbursements.bulkBatch', {
        ns: 'common',
        defaultValue: 'Enter a batch number to identify this disbursement batch. This is required for processing.'
      }),
      position: 'bottom',
      styles: {
        popover: (base) => ({
          ...base,
          marginTop: '10px'
        })
      }
    },
    
    // Bulk Actions - Submit and Cancel buttons
    {
      selector: '[data-tour="disbursements-bulk-actions"]',
      content: t('tour.disbursements.bulkActions', {
        ns: 'common',
        defaultValue: 'Review everything, then submit to process all disbursements, or cancel to start over.'
      }),
      position: 'top',
      styles: {
        popover: (base) => ({
          ...base,
          marginBottom: '10px'
        })
      }
    }
  ];

  // History list (always shown)
  const historyStep = {
    selector: '[data-tour="disbursements-list"]',
    content: t('tour.disbursements.list', {
      ns: 'common',
      defaultValue: 'View and manage past disbursements.'
    }),
    position: 'bottom'
  };

  // Filter steps based on what exists in the DOM
  // Check which tab is active by checking if single form elements exist
  const isSingleTabActive = elementExists('[data-tour="disbursements-source-account"]');
  const isBulkTabActive = elementExists('[data-tour="disbursements-bulk-upload"]') || 
                          elementExists('[data-tour="disbursements-bulk-preview"]');

  // Build steps array based on active tab
  const allSteps = [...baseSteps];
  
  if (isSingleTabActive) {
    // Single Disbursement: Form-based workflow with sections
    // Add single form steps only if on single tab
    allSteps.push(...singleFormSteps.filter(step => elementExists(step.selector)));
  } else if (isBulkTabActive) {
    // Bulk Disbursement: File upload workflow (different from single)
    // Shows upload step when no file, preview step when file uploaded
    // Only add bulk steps if on bulk tab
    allSteps.push(...bulkSteps.filter(step => elementExists(step.selector)));
  }
  
  // Always add history step if it exists (common to both single and bulk)
  if (elementExists(historyStep.selector)) {
    allSteps.push(historyStep);
  }

  // Return filtered steps - only steps for active tab
  return allSteps;
};

